/* eslint-disable @typescript-eslint/no-explicit-any */
import { useAppDispatch, useAppSelector } from '../../hooks.ts';
import {
  addLamp,
  addLampDistanceSelection,
  addLamps,
  addMeasurementPoint,
  addMeshGroup,
  addModel,
  cancelAutoPositioning,
  clearLampDistanceSelection,
  clearMeasurementPoints,
  deleteMeshGroup,
  handleLayerClick,
  openSection,
  redoLampAction,
  removeLamp,
  removeModel,
  renameMeshGroup,
  resetLampHistory,
  resetToDefaultIesProfile,
  selectLamp,
  setActiveToolbarTool,
  setActiveViewMode,
  setDesiredLuminaireHeight,
  setHoveredLayer,
  setLampCreationMode,
  setMeasurementHoverPoint,
  setMeasurementMode,
  setMeasurementVisualizationType,
  setModelLayers,
  setOrthographicView,
  setSelectedLayer,
  setSelectedObject,
  setShowAutoPositionSidebar,
  setShowCompass,
  setShowGrid,
  setShowIESModels,
  setShowMeasurementPoints,
  setShowNearestLampDistancesWhileMoving,
  setShowReferenceImage,
  setShowXYZLines,
  setSidebarOpen,
  setTargetCameraPosition,
  setTransformMode,
  setXLines,
  setYLines,
  setZLines,
  startAutoPositioning,
  startTamanOptimization,
  toggleLayerIllumination,
  toggleLayerLuminaireHolder,
  toggleLayerVisibility,
  toggleMeasurements,
  toggleSection,
  undoLampAction,
  updateLampDistance,
  updateLampName,
  updateLampPosition,
  updateLayerColor,
  updateLayerCustomTargets,
  updateLayerDisplayName,
  updateLayerGroupId,
  updateLayerNorm,
  updateLayerType,
  updateProjectVersion,
  uploadIesProfile,
} from '../../store/reducers/workspace.reducer.ts';
import { MeshGroup } from '../../types/mesh.ts';
import { MeasurementVisualizationType } from '../../types/view.ts';
import type { PhotometricTarget } from '../../utils/photometricTargets';

export const useStore = () => {
  const dispatch = useAppDispatch();
  const state = useAppSelector((state) => state.workspace);
  const activeVersion = useAppSelector((state) => {
    const project = state.workspace.project;
    if (!project || !project.activeVersionId || !project.versions) return undefined;
    return project.versions.find((v) => v.id === project.activeVersionId);
  });

  return {
    ...state,
    activeVersion,
    setShowGrid: (show: boolean) => dispatch(setShowGrid(show)),
    setShowCompass: (show: boolean) => dispatch(setShowCompass(show)),
    setShowReferenceImage: (show: boolean) => dispatch(setShowReferenceImage(show)),
    setOrthographicView: (isOrtho: boolean) => dispatch(setOrthographicView(isOrtho)),
    setShowIESModels: (show: boolean) => dispatch(setShowIESModels(show)),
    setShowNearestLampDistancesWhileMoving: (show: boolean) => dispatch(setShowNearestLampDistancesWhileMoving(show)),
    setShowMeasurementPoints: (show: boolean) => dispatch(setShowMeasurementPoints(show)),
    setShowXYZLines: (show: boolean) => dispatch(setShowXYZLines(show)),
    setXLines: (show: boolean) => dispatch(setXLines(show)),
    setYLines: (show: boolean) => dispatch(setYLines(show)),
    setZLines: (show: boolean) => dispatch(setZLines(show)),
    startTamanOptimization: () => dispatch(startTamanOptimization()),
    autoPositionLights: () => dispatch(startAutoPositioning()),
    cancelAutoPositioning: () => dispatch(cancelAutoPositioning()),
    setDesiredLuminaireHeight: (height: number) => dispatch(setDesiredLuminaireHeight(height)),
    setActiveViewMode: (mode: any) => dispatch(setActiveViewMode(mode)),
    setMeasurementVisualizationType: (type: MeasurementVisualizationType) =>
      dispatch(setMeasurementVisualizationType(type)),
    toggleMeasurements: () => dispatch(toggleMeasurements()),
    uploadIesProfile: (url: string) => dispatch(uploadIesProfile(url)),
    resetToDefaultIesProfile: () => dispatch(resetToDefaultIesProfile()),
    setSelectedObject: (object: any) => dispatch(setSelectedObject(object)),
    setTransformMode: (mode: any) => dispatch(setTransformMode(mode)),
    setSidebarOpen: (open: boolean) => dispatch(setSidebarOpen(open)),
    setShowAutoPositionSidebar: (show: boolean) => dispatch(setShowAutoPositionSidebar(show)),
    setHoveredLayer: (layer: string | null) => dispatch(setHoveredLayer(layer)),
    setSelectedLayer: (layer: string | null) => dispatch(setSelectedLayer(layer)),
    setLampCreationMode: (active: boolean) => dispatch(setLampCreationMode(active)),
    addLamp: (lamp: any) => dispatch(addLamp(lamp)),
    addLamps: (lamps: any[]) => dispatch(addLamps(lamps)),
    removeLamp: (id: string) => dispatch(removeLamp(id)),
    updateLampPosition: (id: string, position: [number, number, number], oldPosition?: [number, number, number]) =>
      dispatch(updateLampPosition({ id, position, oldPosition })),
    undoLampAction: () => dispatch(undoLampAction()),
    redoLampAction: () => dispatch(redoLampAction()),
    resetLampHistory: () => dispatch(resetLampHistory()),
    updateLampDistance: (id: string, distance: number) => dispatch(updateLampDistance({ id, distance })),
    updateLampName: (id: string, name: string) => dispatch(updateLampName({ id, name })),
    selectLamp: (id: string) => dispatch(selectLamp(id)),
    addModel: (model: any) => dispatch(addModel(model)),
    removeModel: (name: string) => dispatch(removeModel(name)),
    setModelLayers: (layers: any[]) => dispatch(setModelLayers(layers)),
    toggleLayerVisibility: (layerName: string) => dispatch(toggleLayerVisibility(layerName)),
    toggleLayerIllumination: (layerName: string) => dispatch(toggleLayerIllumination(layerName)),
    toggleLayerLuminaireHolder: (layerName: string) => dispatch(toggleLayerLuminaireHolder(layerName)),
    updateLayerType: (layerName: string, layerType: any) => dispatch(updateLayerType({ name: layerName, layerType })),
    updateLayerNorm: (layerName: string, norm: string | null) => dispatch(updateLayerNorm({ name: layerName, norm })),
    updateLayerCustomTargets: (layerName: string, targets: PhotometricTarget[]) =>
      dispatch(updateLayerCustomTargets({ name: layerName, targets })),
    updateLayerDisplayName: (layerUuid: string, displayName: string) =>
      dispatch(updateLayerDisplayName({ uuid: layerUuid, displayName })),
    updateLayerColor: (layerUuid: string, color: string) => dispatch(updateLayerColor({ uuid: layerUuid, color })),
    updateLayerGroupId: (layerUuid: string, groupId: string | null) =>
      dispatch(updateLayerGroupId({ uuid: layerUuid, groupId })),
    addMeshGroup: (group: MeshGroup, parentGroupId?: string | null) => dispatch(addMeshGroup({ group, parentGroupId })),
    renameMeshGroup: (uuid: string, name: string) => dispatch(renameMeshGroup({ uuid, name })),
    deleteMeshGroup: (uuid: string) => dispatch(deleteMeshGroup(uuid)),
    updateProjectVersion: (projectId: number, versionId: number, optimisticLockKey: number) =>
      dispatch(updateProjectVersion({ projectId, versionId, optimisticLockKey })),
    toggleSection: (section: string) => dispatch(toggleSection(section)),
    openSection: (section: string) => dispatch(openSection(section)),
    handleLayerClick: (threeJsName: string, originalName: string) =>
      dispatch(handleLayerClick({ threeJsName, originalName })),
    // Measurement tool functions
    setMeasurementMode: (active: boolean) => dispatch(setMeasurementMode(active)),
    addMeasurementPoint: (point: [number, number, number]) => dispatch(addMeasurementPoint(point)),
    clearMeasurementPoints: () => dispatch(clearMeasurementPoints()),
    setActiveToolbarTool: (tool: 'selection' | 'target' | 'measurement' | 'lampDistance' | null) =>
      dispatch(setActiveToolbarTool(tool)),
    setMeasurementHoverPoint: (point: [number, number, number] | null) => dispatch(setMeasurementHoverPoint(point)),
    // Lamp-to-Lamp distance functions
    addLampDistanceSelection: (lampId: string, position: [number, number, number]) =>
      dispatch(addLampDistanceSelection({ lampId, position })),
    clearLampDistanceSelection: () => dispatch(clearLampDistanceSelection()),
    // Camera control functions
    setTargetCameraPosition: (
      target: { position: [number, number, number]; lookAt: [number, number, number] } | null,
    ) => dispatch(setTargetCameraPosition(target)),
  };
};
/* eslint-enable @typescript-eslint/no-explicit-any */
